<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'src/services/i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import { Button, Text, Toggle } from '@Shelly/shelly-design-system';
	import CameraStreamPreview from './CameraStreamPreview.svelte';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import type { CameraVideoOrientationState } from '../../types/camera-settings';

	export let device: Device;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	const readVideo = (): CameraVideoOrientationState => {
		const raw = getCameraConfig(device)?.video;
		return {
			flip: typeof raw?.flip === 'boolean' ? raw.flip : false,
			mirror: typeof raw?.mirror === 'boolean' ? raw.mirror : false,
		};
	};

	let video: CameraVideoOrientationState = readVideo();
	let cache: CameraVideoOrientationState = { ...video };
	let loading = false;

	const AUTOSAVE_DELAY = 500;
	let saveTimeout: ReturnType<typeof setTimeout> | undefined;

	$: hasChanges = video.flip !== cache.flip || video.mirror !== cache.mirror;

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: resetToDefaults,
				loading,
				disabled: loading,
				label: $format('button_reset_to_defaults', { default: 'Reset to defaults' }),
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	const scheduleSave = () => {
		if (saveTimeout) clearTimeout(saveTimeout);
		saveTimeout = setTimeout(save, AUTOSAVE_DELAY);
	};

	const save = async () => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			saveTimeout = undefined;
		}
		if (!hasChanges) return;
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const success = await saveCameraConfig(
			device,
			{ video: { ...currentVideo, ...video } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = { ...video };
		} else {
			video = { ...cache };
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const success = await saveCameraConfig(
			device,
			{ video: { ...currentVideo, flip: null, mirror: null } },
			$format('camera_video_reset_success', { default: 'Video settings reset to defaults.' }),
		);
		if (success) {
			video = readVideo();
			cache = { ...video };
		}
		loading = false;
	};

	onDestroy(() => {
		if (saveTimeout) {
			clearTimeout(saveTimeout);
			save();
		}
		stickyBottomAction?.set(null);
	});
</script>

{#if $showNewLayout}
	<CameraStreamPreview {device} />
{/if}

<div class="mb-s-plus">
	<Toggle
		size="m"
		checked={video.flip}
		disabled={loading}
		on:click={() => {
			video = { ...video, flip: !video.flip };
			scheduleSave();
		}}
		id="camera-video-flip"
	>
		{$format('camera_video_flip', { default: 'Flip image' })}
	</Toggle>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_video_flip_hint', { default: 'Rotate the image 180°.' })}
	</Text>
</div>

<div class="mb-s-plus">
	<Toggle
		size="m"
		checked={video.mirror}
		disabled={loading}
		on:click={() => {
			video = { ...video, mirror: !video.mirror };
			scheduleSave();
		}}
		id="camera-video-mirror"
	>
		{$format('camera_video_mirror', { default: 'Mirror image' })}
	</Toggle>
	<Text styleType="body-extra-small" class="text-color-muted mt-xs d-block">
		{$format('camera_video_mirror_hint', { default: 'Flip the image horizontally.' })}
	</Text>
</div>

{#if !$showNewLayout}
	<Button
		variant="secondary"
		width="full"
		size="m"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{$format('button_reset_to_defaults', { default: 'Reset to defaults' })}
	</Button>
{/if}
